import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentAccountApi, type PaymentAccountsListParams } from '../api/payment-account.api';
import { PaymentAccountInput, PaymentAccountUpdateInput } from '@cms/models';

export function usePaymentAccountsPaginated(params: PaymentAccountsListParams = {}) {
  const { page = 1, pageSize = 25, search, type, status, sortBy, sortOrder } = params;
  return useQuery({
    queryKey: [
      'payment-accounts',
      'paginated',
      page,
      pageSize,
      search ?? '',
      type ?? '',
      status ?? '',
      sortBy ?? '',
      sortOrder ?? '',
    ],
    queryFn: () =>
      paymentAccountApi.getPaginated({
        page,
        pageSize,
        search: search || undefined,
        type,
        status,
        sortBy,
        sortOrder,
      }),
  });
}

export function usePaymentAccount(id: string) {
  return useQuery({
    queryKey: ['payment-account', id],
    queryFn: () => paymentAccountApi.getById(id),
    enabled: !!id && id !== 'new',
  });
}

export function useCreatePaymentAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PaymentAccountInput) => paymentAccountApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['masterdata', 'payment-accounts'] });
    },
  });
}

export function useUpdatePaymentAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PaymentAccountUpdateInput }) =>
      paymentAccountApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['payment-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['payment-account', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['masterdata', 'payment-accounts'] });
    },
  });
}

export function useDeletePaymentAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => paymentAccountApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['masterdata', 'payment-accounts'] });
    },
  });
}
